import { useState } from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { verifyPhoneCodeLandlord, verifyPhoneSignup } from '@/models/auth/ports'
import { breakpoints, colors } from '@/styles'
import { CodeInput } from '@/components/code'
import Countdown from '@/components/countdown'
import { createModal, useModalize } from '@/components/modalize'
import {
  H4,
  ParagraphMedium,
  ParagraphRegular,
  ParagraphSmall
} from '@/components/text'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 24px 52px;
`

const Title = styled(H4)`
  text-align: center;
  max-width: 370px;
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    max-width: 350px;
  }
`

const Description = styled(ParagraphRegular)`
  text-align: center;
  margin: 8px 0 32px;
  color: ${colors.grey56};
`

const ErrorMessage = styled(ParagraphSmall)`
  color: ${colors.primary};
  margin: 12px 0 0;
`

const ResendWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 12px;
`

const FailureText = styled(ParagraphMedium)`
  color: ${colors.grey56};
`

const ResendButton = styled(ParagraphMedium)`
  color: ${colors.link};
  cursor: pointer;
`

const CODE_LENGTH = 4

const VerifyCodeSignupModal = ({ phone, userVerified }) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const [codeSent, setCodeSent] = useState(false)
  const [errorMessage, setErrorMessage] = useState(false)

  const handleSendCode = async () => {
    const { response } = await verifyPhoneSignup({ phone })

    if (response.ok) {
      setCodeSent(true)
    }
  }

  const onSubmit = async ({ code }) => {
    const { body, response } = await verifyPhoneCodeLandlord({
      phone: phone,
      code: code
    })

    if (!response.ok) {
      setErrorMessage(body.message)
      return
    }

    localStorage.setItem('verified-phone-code', code)
    userVerified(phone.toString())
    close()
  }

  return (
    <Wrapper>
      <Title>
        {t(
          'l.auth.view.phone_verification_title',
          "We've sent a verification code to your phone."
        )}
      </Title>
      <Description>
        {t(
          'l.auth.view.phone_verification_description',
          'Building trust and confidence is our priority. We ask all landlords to verify their phones for authentication.'
        )}
      </Description>
      <CodeInput codeLength={CODE_LENGTH} onSubmit={onSubmit} />
      <ErrorMessage>{errorMessage}</ErrorMessage>
      <ResendWrapper>
        <FailureText>
          {t(
            'b.auth.view.code_not_received.text',
            'Did not get the security code?'
          )}{' '}
        </FailureText>
        {codeSent ? (
          <Countdown
            text={t(
              'b.auth.view.resend_code_countdown.text',
              'You can resend in [current] seconds...'
            )}
            delay={60000}
            onComplete={() => setCodeSent(false)}
          />
        ) : (
          <ResendButton onClick={handleSendCode} role="button">
            {t('b.auth.view.resend_code.button', 'Send a new code')}
          </ResendButton>
        )}
      </ResendWrapper>
    </Wrapper>
  )
}

export const show = createModal(VerifyCodeSignupModal, { width: 550 })
